import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { updateFavoriteListings } from '../../ducks/user.duck';
import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { hasFavoriteListingId } from '../../util/favorites';
import { getListingDisplayPrice } from '../../util/listingPricing';
import { useIntl } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';
import { IconHeart, IconSpinner, NamedLink } from '../../components';
import { getCategoryLabel, getSubCategoryLabel } from '../../util/data';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import css from './TrainingProgram.module.css';

const formatCategory = text => {
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const TrainingProgram = ({
  listings,
  fetchPopularListingsInProgress,
  fetchPopularListingsError,
  onCategoryChange,
}) => {
  const config = useConfiguration();
  const intl = useIntl();
  const [allCategory, setAllCategory] = useState([]);
  const [isTabActive, setIsTabActive] = useState(null);
  const hasInitializedCategoryRef = useRef(false);
  const lastFetchedCategoryRef = useRef(null);

  // Build a lookup map from category key -> human-readable Console label
  // Safe fallback: if no config available, returns empty map
  const categoryLabelMap = React.useMemo(() => {
    try {
      const categories = config?.categoryConfiguration?.categories || [];
      return categories.reduce((acc, cat) => {
        if (cat?.id) {
          acc[cat.id] = cat.name || cat.id;
        }
        return acc;
      }, {});
    } catch (e) {
      console.error('[TrainingProgram] Failed to read category config:', e);
      return {};
    }
  }, [config]);

  // Get the display label for a category key
  // const getCategoryLabel = key => {
  //   if (!key) return '';
  //   // Prefer the Console label; fall back to humanizing the key
  //   return categoryLabelMap[key] || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  // };

  // ─── Source of truth for tabs: Sharetribe Console categories ───────────────
  // This ensures any category added in Console automatically appears here,
  // even if no listing has been assigned to it yet.
  const consoleCategoryKeys = React.useMemo(() => {
    try {
      return (config?.categoryConfiguration?.categories || []).map(cat => cat.id).filter(Boolean);
    } catch (e) {
      console.error('[TrainingProgram] Failed to read categoryConfiguration:', e);
      return [];
    }
  }, [config]);

  const categories = config?.categoryConfiguration?.categories;

  useEffect(() => {
    if (consoleCategoryKeys.length > 0) {
      setAllCategory(consoleCategoryKeys);

      // On load, activate the category of the highest-rated listing
      // so the best content is visible immediately
      if (!isTabActive || !consoleCategoryKeys.includes(isTabActive)) {
        const topListingCategory = listings?.[0]?.attributes?.publicData?.categoryLevel1;
        const defaultTab =
          topListingCategory && consoleCategoryKeys.includes(topListingCategory)
            ? topListingCategory
            : consoleCategoryKeys[0];
        setIsTabActive(defaultTab);
      }
    }
  }, [consoleCategoryKeys, isTabActive, listings]);

  useEffect(() => {
    if (!isTabActive || !onCategoryChange) {
      return undefined;
    }

    if (!hasInitializedCategoryRef.current) {
      hasInitializedCategoryRef.current = true;
      lastFetchedCategoryRef.current = isTabActive;
      return undefined;
    }

    if (lastFetchedCategoryRef.current === isTabActive) {
      return undefined;
    }

    let isMounted = true;
    lastFetchedCategoryRef.current = isTabActive;

    Promise.resolve(onCategoryChange(isTabActive)).catch(error => {
      if (isMounted) {
        console.error('[TrainingProgram] Failed to refresh listings for category:', {
          categoryLevel1: isTabActive,
          error,
        });
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isTabActive, onCategoryChange]);

  const activeListing = listings?.filter(
    l => l.attributes?.publicData?.categoryLevel1 === isTabActive
  );
  const formatListingPrice = listing => {
    const resolvedPrice = getListingDisplayPrice(listing, config?.currency);

    if (!resolvedPrice) {
      return null;
    }

    try {
      return formatMoney(intl, resolvedPrice);
    } catch (error) {
      console.error('Failed to format landing page listing price.', {
        listingId: listing?.id?.uuid,
        error,
      });
      return null;
    }
  };

  const currentUser = useSelector(state => state.user?.currentUser);
  const updateFavoriteListingsInProgress = useSelector(
    state => state.user?.updateFavoriteListingsInProgress
  );
  const updateFavoriteListingsError = useSelector(state => state.user?.updateFavoriteListingsError);
  const dispatch = useDispatch();

  const isCustomer = currentUser && getCurrentUserTypeRoles(config, currentUser).customer;

  return (
    <div className={css.trainingProgramWrapper}>
      {updateFavoriteListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="SearchPage.favoritesUpdateError" />
        </p>
      ) : null}
      {/* Tab menu heading */}
      {allCategory.length === 0 && fetchPopularListingsInProgress ? (
        // Loading state: preserve the visual space with skeleton tabs
        // so the section title doesn't jump when categories load in
        <div className={css.tabMenuContainer}>
          <div className={css.tabMenu}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={css.tabSkeleton}>
                <div className={css.tabSkeletonIcon} />
                <div className={css.tabSkeletonLabel} />
              </div>
            ))}
          </div>
        </div>
      ) : allCategory.length > 0 ? (
        // Ready state: real tabs from Sharetribe Console
        <div className={css.tabMenuContainer}>
          <div className={css.tabMenu}>
            {allCategory.map(item => (
              <div
                key={item}
                onClick={() => {
                  if (item !== isTabActive) {
                    setIsTabActive(item);
                  }
                }}
                className={classNames(css.tab, isTabActive === item ? css.activeTab : '')}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' && item !== isTabActive) {
                    setIsTabActive(item);
                  }
                }}
              >
                <div className={css.iconContainer}>
                  <IconCard brand={item} />
                </div>
                <span className={css.label}>{getCategoryLabel(categories, item)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null /* No empty box when not loading and no categories */}

      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Programmes de formation populaires</h2>
        <NamedLink
          name="SearchPage"
          to={{ search: isTabActive ? `pub_categoryLevel1=${isTabActive}` : '' }}
          href="/"
          className={css.viewAllBtn}
        >
          <FormattedMessage id="TrainingProgram.viewAll" />{' '}
          {isTabActive ? getCategoryLabel(categories, isTabActive) : ''}
        </NamedLink>
      </div>

      {fetchPopularListingsInProgress ? (
        <div className={css.loading}>
          <IconSpinner />
        </div>
      ) : fetchPopularListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="TrainingProgram.fetchError" />
        </p>
      ) : (
        /* card section */
        <div className={css.courseContainer}>
          {activeListing?.length > 0 ? (
            activeListing?.slice(0, 4)?.map(listing => {
              const publicData = listing?.attributes?.publicData || {};
              const metadata = listing?.attributes?.metadata || {};
              const formattedPrice = formatListingPrice(listing);
              const category = listing.attributes.publicData.categoryLevel1 || '-';
              const image =
                listing?.images[0]?.attributes?.variants?.['listing-card-2x']?.url || '';
              const userDetail = listing?.author?.attributes?.profile?.displayName || '-';
              const listingTitle = listing?.attributes?.title || '-';
              const listingUid = listing?.id?.uuid;
              const linkToListing = listingUid
                ? `/l/${createSlug(listingTitle)}/${listingUid}`
                : '/';

              const { categoryLevel1, categoryLevel2 } = publicData;

              return (
                <a href={linkToListing} className={css.card} key={listingUid}>
                  {isCustomer ? (
                    <button
                      type="button"
                      className={classNames(css.likeBtn, {
                        [css.likeBtnActive]: hasFavoriteListingId(currentUser, listing?.id),
                      })}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        dispatch(updateFavoriteListings(listing?.id));
                      }}
                      disabled={updateFavoriteListingsInProgress}
                      aria-label={intl.formatMessage({ id: 'SearchPage.toggleFavorite' })}
                    >
                      <IconHeart
                        isFilled={hasFavoriteListingId(currentUser, listing?.id)}
                        ariaLabel={intl.formatMessage({ id: 'SearchPage.toggleFavorite' })}
                      />
                    </button>
                  ) : null}
                  <div className={css.imgContainer}>
                    <img src={image} alt={listing.attributes.title} />
                  </div>

                  <div className={css.cardInfo}>
                    <div>
                      <span className={css.courseLabel}>
                        {getCategoryLabel(categories, categoryLevel1)} -{' '}
                        {getSubCategoryLabel(categories, categoryLevel1, categoryLevel2)}
                      </span>
                      <h4 className={css.courseTitle}>{listingTitle}</h4>
                    </div>

                    <div className={css.rating}>
                      <IconCard brand="ratingStar" /> {metadata.averageRating || 0.0}
                    </div>
                  </div>

                  <div className={css.courceDetails}>
                    <p className={css.author}>
                      Par <span>{userDetail}</span>
                    </p>
                    {formattedPrice ? (
                      <p className={css.price}>
                        <span className={css.price}>{formattedPrice}</span>
                      </p>
                    ) : null}
                  </div>
                </a>
              );
            })
          ) : (
            // Empty state: category exists in Console but has no listings yet
            <p className={css.emptyCategory}>
              <FormattedMessage id="TrainingProgram.noCategoryListings" />
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainingProgram;
